import { EcgEventType } from '../eventECGTypeModel';
import { Role } from '../roleModel';

export interface NotificationType {
  id: string;
  name: string;
}

export interface RuleTemplate {
  emailTemplate: string;
  thirdPartyEmailTemplate: string;
  smsTemplate: string;
  thirdPartySmsTemplate: string;
}

export interface RuleCommon {
  id?: string;
  notifyType?: string[];
  notifyInterval?: number;
  notifyIntervalUnit?: string;
  emailTemplate?: string;
  smsTemplate?: string;
  thirdPartyEmailTemplate?: string;
  thirdPartySmsTemplate?: string;
}

export interface EventRule extends RuleCommon {
  key: string;
  eventType: string | EcgEventType;
  color: string;
  setFlag: boolean | string;
  notifyRoles?: Role[] | string[];
}

export interface HeartRateRule extends RuleCommon {
  notifyRoles?: Role[] | string[];
  beats: string | number;
  color: string;
  duration: string | number;
  durationType: string;
  key: string;
  ruleType: string;
  setFlag: boolean | string;
}

export type HeartRateRuleTemplate = RuleTemplate;

export interface ECGInactivityAlertRule extends RuleCommon {
  key: string;
  color: string;
  duration: string | number;
  setFlag: boolean | string;
  notifyRoles?: Role[] | string[];
}

export interface SpO2InactivityAlertRule extends RuleCommon {
  key: string;
  color: string;
  duration: string | number;
  setFlag: boolean | string;
  notifyRoles?: Role[] | string[];
}

export interface TemperatureInactivityAlertRule extends RuleCommon {
  key: string;
  color: string;
  duration: string | number;
  setFlag: boolean | string;
  notifyRoles?: Role[] | string[];
}

export interface BPInactivityAlertRule extends RuleCommon {
  key: string;
  color: string;
  count: string | number;
  duration: string | number;
  setFlag: boolean | string;
  notifyRoles?: Role[] | string[];
}

export interface MultipleEventsRule extends RuleCommon {
  key: string;
  eventType?: string | EcgEventType;
  count: string | number;
  durationType: string;
  duration: string | number;
  color: string;
  setFlag: boolean | string;
  notifyRoles?: Role[] | string[];
}

export interface BPRule extends RuleCommon {
  notifyRoles?: Role[] | string[];
  color: string;
  value: string | number;
  type: string;
  key: string;
  ruleType: string;
  setFlag: boolean | string;
}

export interface TemperatureRule extends RuleCommon {
  notifyRoles?: Role[] | string[];
  value: string | number;
  color: string;
  duration: string | number;
  durationType: string;
  key: string;
  ruleType: string;
  setFlag: boolean | string;
}

export interface TimezoneRule extends RuleCommon {
  notifyRoles?: Role[] | string[];
  value: string | number;
  color: string;
  key: string;
  ruleType: string;
  setFlag: boolean | string;
}

export interface SpO2Rule extends RuleCommon {
  notifyRoles?: Role[] | string[];
  value: string | number;
  color: string;
  key: string;
  ruleType: string;
  setFlag: boolean | string;
}

export interface CGMTimeOutOfRangeRuleValue {
  param1condition: string;
  param1Value?: string | number;
  param2condition: string;
  param2Value?: string | number;
}

export interface CGMTimeOutOfRangeRule extends RuleCommon {
  key: string;
  type: string;
  value: CGMTimeOutOfRangeRuleValue;
  proportion: string | number;
  durationType: string;
  duration: string | number;
  color: string;
  setFlag: boolean | string;
  notifyRoles?: Role[] | string[];
}

export interface CGMBloodGlucoseLevelRule extends RuleCommon {
  key: string;
  type: string;
  value: string | number;
  durationType: string;
  duration: string | number;
  color: string;
  setFlag: boolean | string;
  notifyRoles?: Role[] | string[];
}

export interface CGMMultipleEventsRule extends RuleCommon {
  key: string;
  eventType?: string | EcgEventType;
  count: string | number;
  durationType: string;
  duration: string | number;
  color: string;
  setFlag: boolean | string;
  notifyRoles?: Role[] | string[];
}

export interface RPMVitalAlertRuleValue {
  param1condition: string;
  param1value?: string | number;
  param2condition: string;
  param2value?: string | number;
}

export interface RPMVitalAlertRule extends RuleCommon {
  key: string;
  rowKey?: number;
  type?: string;
  alertThresholdAndAveragingDuration?: string;
  rangeValue?: CGMTimeOutOfRangeRuleValue;
  averagingDuration?: number;
  escalationInterval?: number;
  slidingLength?: number;
  alertColorFlag?: string;
  alertSeverity?: string;
  notifyRoles?: Role[] | string[];
  children?: RPMVitalAlertRule[];
}

export interface RPMDeviceInactivityAlertRule extends RuleCommon {
  key: string;
  type: string;
  duration: string | number;
  escalationInterval?: number;
  notifyRoles?: Role[] | string[];
}

export interface RPMDeviceLowBatteryAlertRule extends RuleCommon {
  key: string;
  type: string;
  lowValue: string | number;
  mediumValue: string | number;
  escalationInterval?: number;
  notifyRoles?: Role[] | string[];
}

export interface FallDetectionAlertRule extends RuleCommon {
  key: string;
  isEnabled: boolean;
  color: string;
  severityLevel: string;
  notifyRoles?: Role[] | string[];
}

export interface MonitoringProtocol {
  id: string;
  name: string;
  notifyInterval: number;
  rpmDelayLimit?: number;
  updatedAt?: string;
  ranking?: number;
  prioritizationScore?: number;
  eventRules: { list: EventRule[] };
  heartRateRules: { list: HeartRateRule[] };
  multipleEventsRules?: { list: MultipleEventsRule[] } | undefined;
  ecgInactivityAlertRules: { list: ECGInactivityAlertRule[] };
  spo2InactivityAlertRules: { list: SpO2InactivityAlertRule[] };
  temperatureInactivityAlertRules: { list: TemperatureInactivityAlertRule[] };
  bpInactivityAlertRules: { list: BPInactivityAlertRule[] };
  bpRules: { list: BPRule[] };
  temperatureRules: { list: TemperatureRule[] };
  timeZoneChangeRules: { list: TimezoneRule[] };
  spo2Rules: { list: SpO2Rule[] };
  cgmTimeOutOfRangeRules: { list: CGMTimeOutOfRangeRule[] };
  cgmBloodGlucoseLevelRules: { list: CGMBloodGlucoseLevelRule[] };
  cgmMultipleEventsRules: { list: CGMMultipleEventsRule[] };
  rpmVitalAlertRules: { list: RPMVitalAlertRule[] };
  rpmDeviceInactivityAlertRules: { list: RPMDeviceInactivityAlertRule[] };
  rpmDeviceLowBatteryAlertRules: { list: RPMDeviceLowBatteryAlertRule[] };
  rpmFallDetectionAlertRules: { list: FallDetectionAlertRule[] };
  parentId: string;
  customStatus: boolean;
  patientId: string;
  customStatusDisabled: boolean;
  eventRuleTemplate: RuleTemplate;
  heartRateRuleTemplate: RuleTemplate;
  multipleEventsRuleTemplate: RuleTemplate;
  ecgInactivityAlertRuleTemplate: RuleTemplate;
  spo2InactivityAlertRuleTemplate: RuleTemplate;
  temperatureInactivityAlertRuleTemplate: RuleTemplate;
  bpInactivityAlertRuleTemplate: RuleTemplate;
  bpRuleTemplate: RuleTemplate;
  temperatureRuleTemplate: RuleTemplate;
  timeZoneChangeRuleTemplate: RuleTemplate;
  spo2RuleTemplate: RuleTemplate;
  cgmTimeOutOfRangeRuleTemplate: RuleTemplate;
  cgmBloodGlucoseLevelRuleTemplate: RuleTemplate;
  cgmMultipleEventsRuleTemplate: RuleTemplate;
  rpmVitalAlertRuleTemplate: RuleTemplate;
  rpmDeviceInactivityAlertRuleTemplate: RuleTemplate;
  rpmDeviceLowBatteryAlertRuleTemplate: RuleTemplate;
  rpmFallDetectionAlertRuleTemplate: RuleTemplate;
  isQuickPatientAssignment: boolean;
}

export interface RawMonitoringProtocol {
  id: string;
  name: string;
  notifyInterval: number;
  rpmDelayLimit?: number;
  updatedAt?: string;
  ranking?: number;
  prioritizationScore?: number;
  eventRules: { list: EventRule[] };
  heartRateRules: { list: HeartRateRule[] };
  multipleEventsRules?: { list: MultipleEventsRule[] } | undefined;
  ecgInactivityAlertRules: { list: ECGInactivityAlertRule[] };
  spo2InactivityAlertRules: { list: SpO2InactivityAlertRule[] };
  temperatureInactivityAlertRules: { list: TemperatureInactivityAlertRule[] };
  bpInactivityAlertRules: { list: BPInactivityAlertRule[] };
  bpRules: { list: BPRule[] };
  temperatureRules: { list: TemperatureRule[] };
  timeZoneChangeRules: { list: TimezoneRule[] };
  spo2Rules: { list: SpO2Rule[] };
  cgmTimeOutOfRangeRules: { list: CGMTimeOutOfRangeRule[] };
  cgmBloodGlucoseLevelRules: { list: CGMBloodGlucoseLevelRule[] };
  cgmMultipleEventsRules: { list: CGMMultipleEventsRule[] };
  rpmVitalAlertRules: { list: RPMVitalAlertRule[] };
  rpmDeviceInactivityAlertRules: { list: RPMDeviceInactivityAlertRule[] };
  rpmDeviceLowBatteryAlertRules: { list: RPMDeviceLowBatteryAlertRule[] };
  rpmFallDetectionAlertRules: { list: FallDetectionAlertRule[] };
  customStatus: boolean;
  parentId: string;
  patientId: string;
  customStatusDisabled: boolean;
  eventRuleTemplate: RuleTemplate;
  heartRateRuleTemplate: RuleTemplate;
  multipleEventsRuleTemplate: RuleTemplate;
  ecgInactivityAlertRuleTemplate: RuleTemplate;
  spo2InactivityAlertRuleTemplate: RuleTemplate;
  temperatureInactivityAlertRuleTemplate: RuleTemplate;
  bpInactivityAlertRuleTemplate: RuleTemplate;
  bpRuleTemplate: RuleTemplate;
  temperatureRuleTemplate: RuleTemplate;
  timeZoneChangeRuleTemplate: RuleTemplate;
  spo2RuleTemplate: RuleTemplate;
  cgmTimeOutOfRangeRuleTemplate: RuleTemplate;
  cgmBloodGlucoseLevelRuleTemplate: RuleTemplate;
  cgmMultipleEventsRuleTemplate: RuleTemplate;
  rpmVitalAlertRuleTemplate: RuleTemplate;
  rpmDeviceInactivityAlertRuleTemplate: RuleTemplate;
  rpmDeviceLowBatteryAlertRuleTemplate: RuleTemplate;
  rpmFallDetectionAlertRuleTemplate: RuleTemplate;
  isQuickPatientAssignment: boolean;
}

export const MONITORING_PROTOCOL_TITLE = 'Monitoring Protocol';

export interface RankingModel {
  total: number;
  ranking: number;
}

export class MonitoringProtocolModel implements MonitoringProtocol {
  id: string;

  name: string;

  notifyInterval: number;

  rpmDelayLimit?: number;

  updatedAt?: string;

  ranking?: number;

  prioritizationScore?: number;

  eventRules: { list: EventRule[] };

  heartRateRules: { list: HeartRateRule[] };

  ecgInactivityAlertRules: { list: ECGInactivityAlertRule[] };

  spo2InactivityAlertRules: { list: SpO2InactivityAlertRule[] };

  temperatureInactivityAlertRules: { list: TemperatureInactivityAlertRule[] };

  bpInactivityAlertRules: { list: BPInactivityAlertRule[] };

  multipleEventsRules?: { list: MultipleEventsRule[] } | undefined;

  bpRules: { list: BPRule[] };

  temperatureRules: { list: TemperatureRule[] };

  timeZoneChangeRules: { list: TimezoneRule[] };

  spo2Rules: { list: SpO2Rule[] };

  cgmTimeOutOfRangeRules: { list: CGMTimeOutOfRangeRule[] };

  cgmBloodGlucoseLevelRules: { list: CGMBloodGlucoseLevelRule[] };

  cgmMultipleEventsRules: { list: CGMMultipleEventsRule[] };

  rpmVitalAlertRules: { list: RPMVitalAlertRule[] };

  rpmDeviceInactivityAlertRules: { list: RPMDeviceInactivityAlertRule[] };

  rpmDeviceLowBatteryAlertRules: { list: RPMDeviceLowBatteryAlertRule[] };

  rpmFallDetectionAlertRules: { list: FallDetectionAlertRule[] };

  customStatus: boolean;

  parentId: string;

  patientId: string;

  customStatusDisabled: boolean;

  eventRuleTemplate: RuleTemplate;

  heartRateRuleTemplate: RuleTemplate;

  multipleEventsRuleTemplate: RuleTemplate;

  ecgInactivityAlertRuleTemplate: RuleTemplate;

  spo2InactivityAlertRuleTemplate: RuleTemplate;

  temperatureInactivityAlertRuleTemplate: RuleTemplate;

  bpInactivityAlertRuleTemplate: RuleTemplate;

  bpRuleTemplate: RuleTemplate;

  temperatureRuleTemplate: RuleTemplate;

  timeZoneChangeRuleTemplate: RuleTemplate;

  spo2RuleTemplate: RuleTemplate;

  cgmTimeOutOfRangeRuleTemplate: RuleTemplate;

  cgmBloodGlucoseLevelRuleTemplate: RuleTemplate;

  cgmMultipleEventsRuleTemplate: RuleTemplate;

  rpmVitalAlertRuleTemplate: RuleTemplate;

  rpmDeviceInactivityAlertRuleTemplate: RuleTemplate;

  rpmDeviceLowBatteryAlertRuleTemplate: RuleTemplate;

  rpmFallDetectionAlertRuleTemplate: RuleTemplate;

  isQuickPatientAssignment: boolean;

  constructor(data: RawMonitoringProtocol) {
    this.id = data.id;
    this.name = data.name;
    this.notifyInterval = data.notifyInterval;
    this.rpmDelayLimit = data.rpmDelayLimit;
    this.updatedAt = data.updatedAt;
    this.ranking = data.ranking;
    this.prioritizationScore = data.prioritizationScore;
    this.eventRules = data.eventRules;
    this.heartRateRules = data.heartRateRules;
    this.ecgInactivityAlertRules = data.ecgInactivityAlertRules;
    this.spo2InactivityAlertRules = data.spo2InactivityAlertRules;
    this.temperatureInactivityAlertRules = data.temperatureInactivityAlertRules;
    this.bpInactivityAlertRules = data.bpInactivityAlertRules;
    this.multipleEventsRules = data.multipleEventsRules || undefined;
    this.bpRules = data.bpRules;
    this.temperatureRules = data.temperatureRules;
    this.timeZoneChangeRules = data.timeZoneChangeRules;
    this.spo2Rules = data.spo2Rules;
    this.cgmTimeOutOfRangeRules = data.cgmTimeOutOfRangeRules;
    this.cgmBloodGlucoseLevelRules = data.cgmBloodGlucoseLevelRules;
    this.cgmMultipleEventsRules = data.cgmMultipleEventsRules;
    this.rpmVitalAlertRules = data.rpmVitalAlertRules;
    this.rpmDeviceInactivityAlertRules = data.rpmDeviceInactivityAlertRules;
    this.rpmDeviceLowBatteryAlertRules = data.rpmDeviceLowBatteryAlertRules;
    this.rpmFallDetectionAlertRules = data.rpmFallDetectionAlertRules;
    this.customStatus = data.customStatus;
    this.parentId = data.parentId;
    this.patientId = data.patientId;
    this.customStatusDisabled = data.customStatusDisabled;
    this.eventRuleTemplate = data.eventRuleTemplate;
    this.heartRateRuleTemplate = data.heartRateRuleTemplate;
    this.multipleEventsRuleTemplate = data.multipleEventsRuleTemplate;
    this.ecgInactivityAlertRuleTemplate = data.ecgInactivityAlertRuleTemplate;
    this.spo2InactivityAlertRuleTemplate = data.spo2InactivityAlertRuleTemplate;
    this.temperatureInactivityAlertRuleTemplate =
      data.temperatureInactivityAlertRuleTemplate;
    this.bpInactivityAlertRuleTemplate = data.bpInactivityAlertRuleTemplate;
    this.bpRuleTemplate = data.bpRuleTemplate;
    this.temperatureRuleTemplate = data.temperatureRuleTemplate;
    this.timeZoneChangeRuleTemplate = data.timeZoneChangeRuleTemplate;
    this.spo2RuleTemplate = data.spo2RuleTemplate;
    this.cgmTimeOutOfRangeRuleTemplate = data.cgmTimeOutOfRangeRuleTemplate;
    this.cgmBloodGlucoseLevelRuleTemplate =
      data.cgmBloodGlucoseLevelRuleTemplate;
    this.cgmMultipleEventsRuleTemplate = data.cgmMultipleEventsRuleTemplate;
    this.rpmVitalAlertRuleTemplate = data.rpmVitalAlertRuleTemplate;
    this.rpmDeviceInactivityAlertRuleTemplate =
      data.rpmDeviceInactivityAlertRuleTemplate;
    this.rpmDeviceLowBatteryAlertRuleTemplate =
      data.rpmDeviceLowBatteryAlertRuleTemplate;
    this.isQuickPatientAssignment = data.isQuickPatientAssignment;
    this.rpmFallDetectionAlertRuleTemplate =
      data.rpmFallDetectionAlertRuleTemplate;
  }
}
